import type { Clock } from './types.js';

export interface HlcState {
  readonly wall: number;
  readonly counter: number;
  readonly node: string;
}

const WALL_DIGITS = 15;
const COUNTER_DIGITS = 3;
const COUNTER_MAX = 36 ** COUNTER_DIGITS - 1;
const NODE_RE = /^[A-Za-z0-9_-]{1,64}$/;
const CLOCK_RE = /^\d{15}-[0-9a-z]{3}-[A-Za-z0-9_-]{1,64}$/;

/** A remote clock further ahead than this is a broken device clock, not a real event. */
export const MAX_CLOCK_DRIFT_MS = 60 * 60 * 1000;

export class ClockDriftError extends Error {
  constructor(
    public readonly clock: Clock,
    public readonly driftMs: number,
  ) {
    super(`remote clock ${clock} is ${driftMs} ms ahead of local time`);
    this.name = 'ClockDriftError';
  }
}

export function isClock(value: unknown): value is Clock {
  return typeof value === 'string' && CLOCK_RE.test(value);
}

export function hlcInit(node: string): HlcState {
  if (!NODE_RE.test(node)) throw new Error(`invalid clock node id: ${node}`);
  return { wall: 0, counter: 0, node };
}

export function hlcFormat(wall: number, counter: number, node: string): Clock {
  return `${String(wall).padStart(WALL_DIGITS, '0')}-${counter.toString(36).padStart(COUNTER_DIGITS, '0')}-${node}`;
}

export function hlcParse(clock: Clock): { wall: number; counter: number; node: string } {
  if (!isClock(clock)) throw new Error(`malformed clock: ${clock}`);
  return {
    wall: Number(clock.slice(0, WALL_DIGITS)),
    counter: parseInt(clock.slice(WALL_DIGITS + 1, WALL_DIGITS + 1 + COUNTER_DIGITS), 36),
    node: clock.slice(WALL_DIGITS + COUNTER_DIGITS + 2),
  };
}

export function clockWall(clock: Clock): number {
  return Number(clock.slice(0, WALL_DIGITS));
}

export function clockNode(clock: Clock): string {
  return clock.slice(WALL_DIGITS + COUNTER_DIGITS + 2);
}

export function compareClocks(a: Clock, b: Clock): -1 | 0 | 1 {
  return a < b ? -1 : a > b ? 1 : 0;
}

export function maxClock(a: Clock | null | undefined, b: Clock | null | undefined): Clock | null {
  if (!a) return b ?? null;
  if (!b) return a;
  return a > b ? a : b;
}

function settle(wall: number, counter: number): { wall: number; counter: number } {
  // Counter overflow: pretend a millisecond passed. Ordering stays monotonic.
  return counter > COUNTER_MAX ? { wall: wall + 1, counter: 0 } : { wall, counter };
}

/** A local event: returns the clock to stamp on it and the advanced state. */
export function hlcTick(state: HlcState, now: number): { state: HlcState; clock: Clock } {
  const physical = Math.floor(now);
  const wall = Math.max(state.wall, physical);
  const counter = wall === state.wall ? state.counter + 1 : 0;
  const next = { ...state, ...settle(wall, counter) };
  return { state: next, clock: hlcFormat(next.wall, next.counter, next.node) };
}

/** A remote clock was observed: the local state moves past it so later local events sort after it. */
export function hlcReceive(state: HlcState, remote: Clock, now: number): HlcState {
  const r = hlcParse(remote);
  const physical = Math.floor(now);
  const drift = r.wall - physical;
  if (drift > MAX_CLOCK_DRIFT_MS) throw new ClockDriftError(remote, drift);
  const wall = Math.max(state.wall, r.wall, physical);
  let counter: number;
  if (wall === state.wall && wall === r.wall) counter = Math.max(state.counter, r.counter) + 1;
  else if (wall === state.wall) counter = state.counter + 1;
  else if (wall === r.wall) counter = r.counter + 1;
  else counter = 0;
  return { ...state, ...settle(wall, counter) };
}
